<template>
  <div class="detail-view">
    <a-spin :spinning="loading">
      <template v-for="(section, index) in sections" :key="index">
        <DetailSection
          :title="section.title"
          :icon="section.icon"
          :bordered="section.bordered"
        >
          <a-descriptions
            v-if="section.type === 'descriptions'"
            :column="section.column || 2"
            :bordered="section.bordered"
          >
            <a-descriptions-item
              v-for="field in section.fields"
              :key="field.key"
              :label="field.label"
              :span="field.span"
            >
              <slot
                :name="`field-${field.key}`"
                :value="getFieldValue(field.key)"
                :record="data"
              >
                <component
                  v-if="field.component"
                  :is="field.component"
                  :value="getFieldValue(field.key)"
                  :record="data"
                />
                <span v-else>{{ formatFieldValue(field) }}</span>
              </slot>
            </a-descriptions-item>
          </a-descriptions>

          <div v-else-if="section.type === 'custom'" class="custom-section">
            <slot :name="`section-${index}`" :data="data" />
          </div>
        </DetailSection>
      </template>

      <!-- 操作按钮 -->
      <div v-if="actions && actions.length > 0" class="detail-actions">
        <a-space>
          <template v-for="action in actions" :key="action.key">
            <a-button
              :type="action.type || 'default'"
              :danger="action.danger"
              :loading="action.loading"
              @click="handleAction(action)"
            >
              <template v-if="action.icon" #icon>
                <component :is="action.icon" />
              </template>
              {{ action.label }}
            </a-button>
          </template>
        </a-space>
      </div>
    </a-spin>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import DetailSection from './DetailSection.vue'
import { formatDateTime, formatFileSize } from '@/shared/utils'

/**
 * 详情字段配置
 */
export interface DetailField {
  /** 字段键 */
  key: string
  /** 字段标签 */
  label: string
  /** 字段类型 */
  type?: 'text' | 'date' | 'datetime' | 'fileSize' | 'number' | 'custom'
  /** 格式化函数 */
  formatter?: (value: any, record: any) => string
  /** 自定义组件 */
  component?: any
  /** 列跨度 */
  span?: number
}

/**
 * 详情区块配置
 */
export interface DetailSection {
  /** 区块标题 */
  title: string
  /** 区块图标 */
  icon?: string
  /** 区块类型 */
  type?: 'descriptions' | 'custom'
  /** 字段配置（当type为descriptions时使用） */
  fields?: DetailField[]
  /** 列数（当type为descriptions时使用） */
  column?: number
  /** 是否显示边框 */
  bordered?: boolean
}

/**
 * 操作按钮配置
 */
export interface DetailAction {
  /** 操作键 */
  key: string
  /** 操作标签 */
  label: string
  /** 按钮类型 */
  type?: 'default' | 'primary' | 'dashed' | 'link'
  /** 是否危险操作 */
  danger?: boolean
  /** 加载状态 */
  loading?: boolean
  /** 图标 */
  icon?: string
}

interface Props {
  /** 数据对象 */
  data: Record<string, any>
  /** 区块配置 */
  sections: DetailSection[]
  /** 操作按钮配置 */
  actions?: DetailAction[]
  /** 加载状态 */
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
})

const emit = defineEmits<{
  action: [action: string, data: Record<string, any>]
}>()

// 获取字段值
const getFieldValue = (key: string): any => {
  const keys = key.split('.')
  let value = props.data
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k]
    } else {
      return undefined
    }
  }
  return value
}

// 格式化字段值
const formatFieldValue = (field: DetailField): string => {
  const value = getFieldValue(field.key)
  
  if (value === null || value === undefined) {
    return '-'
  }
  
  // 使用自定义格式化函数
  if (field.formatter) {
    return field.formatter(value, props.data)
  }
  
  // 根据类型格式化
  switch (field.type) {
    case 'date':
      return formatDateTime(value, 'YYYY-MM-DD')
    case 'datetime':
      return formatDateTime(value)
    case 'fileSize':
      return formatFileSize(value)
    case 'number':
      return typeof value === 'number' ? value.toLocaleString() : String(value)
    default:
      return String(value)
  }
}

// 处理操作
const handleAction = (action: DetailAction) => {
  emit('action', action.key, props.data)
}
</script>

<style scoped lang="less">
.detail-view {
  .detail-actions {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid #f0f0f0;
  }
}
</style>

